<script setup lang="ts">
    import { computed, ref, useSlots } from 'vue';
    import ChatMessageAuthor from './ChatMessageAuthor.vue';
    import TimeLabel from './TimeLabel.vue';
    import RateMessage from './RateMessage.vue';
    import { IChatMessage } from '@/js/services/api/chat/chat';
    import ChatMessageReasoning from './ChatMessageReasoning.vue';
    import { EllipsisIcon } from '@lucide/vue';
    import { __ } from '@/js/stores/expressions';
    import IconButton from '../IconButton.vue';

    const props = withDefaults(
        defineProps<{
            message: IChatMessage;
            rating?: boolean;
            timestamp?: boolean;
            isLast?: boolean;
        }>(),
        {
            rating: true,
            timestamp: true,
            isLast: false,
        }
    );

    const showReasoning = ref(false);

    defineOptions({
        inheritAttrs: false,
    });
    const slots = useSlots();

    const shouldShowReasoning = computed(() => {
        const msg = props.message;
        return (
            !!msg.reasoning &&
            ((!msg.isUserMessage && msg.messageType !== 'Clarification') ||
                (msg.isUserMessage && msg.messageType === 'Prompt'))
        );
    });
</script>

<template>
    <li
        class="Chat-message @container"
        :class="'Chat-message--' + (message.isUserMessage ? 'user' : 'assistant')"
        role="listitem"
    >
        <ChatMessageAuthor :role="message.isUserMessage ? 'user' : 'assistant'" />
        <div class="Chat-message__wrapper">
            <div class="Chat-message__content" v-bind="$attrs">
                <slot />
            </div>
            <div v-if="slots.after" class="Chat-message__after">
                <slot name="after" />
            </div>
            <div class="Chat-message__footer">
                <slot v-if="slots.footer" name="footer" />

                <TimeLabel v-if="timestamp" :timestamp="new Date(message.timestamp as string)" />

                <RateMessage v-if="rating" :message="message" />

                <IconButton
                    v-if="shouldShowReasoning"
                    size="size-4"
                    area-size="size-5"
                    :tooltip="__('show-reasoning')"
                    @click="showReasoning = !showReasoning"
                >
                    <EllipsisIcon class="size-4" />
                </IconButton>
            </div>
            <transition name="fade">
                <ChatMessageReasoning
                    v-if="shouldShowReasoning"
                    :message="message"
                    :show="showReasoning"
                    :is-last="props.isLast"
                    @close="showReasoning = false"
                />
            </transition>
        </div>
    </li>
</template>
